import { useEffect, useState } from "react";
import { useMap, Map, AdvancedMarker, InfoWindow } from "@vis.gl/react-google-maps";
import { Link } from "react-router-dom";

import API from "../API";
import { InventoryWarehouse } from "../SiteClasses";
import { formatFullAddressMX } from "../SiteUtil";
import { Header, Icon, Button } from "react-frontier-v2";
import { useBreadcrumbs } from "../hooks";

const DEFAULT_CENTER = { lat: 23.634501, lng: -102.552784 };
const DEFAULT_ZOOM = 7;

var WarehousesMap = ()=>{
   var map = useMap();
   var { setTitle } = useBreadcrumbs();
   
   var [loadError, setLoadError] = useState<string>(null);
   var [warehouses, setWarehouses] = useState<InventoryWarehouse[]>(null);
   var [selectedWarehouse, setSelectedWarehouse] = useState<InventoryWarehouse>(null);

   var resetView=()=>{
      if(!map) return;
      map.panTo(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
      setSelectedWarehouse(null);
   }

   var get = ()=>{
      API.getWarehouses().then(res=>{
         if(res.error) return setLoadError(res.message);
         setWarehouses(res.data);
      }).catch(()=>{
         setLoadError('Hubo un error inesperado cargando los almacenes.');
      })
   };

   // eslint-disable-next-line react-hooks/exhaustive-deps
   useEffect(()=>setTitle('Mapa de almacenes'), []);
   useEffect(()=>get(), []);

   var goPosition = (lat: number, lng: number)=>{
      map.panTo({ lat, lng });
      google.maps.event.addListenerOnce(map, 'idle', () => {
         map.setZoom(16);
      });
   }

   if(loadError && !warehouses) return <Header text={'Error'} subtext={loadError} />
   if(!loadError && !warehouses) return <Header loading text={'Cargando almacenes'} />

   var getMarkerColor = (capacity: number | string | undefined, maletines: number | string | undefined)=>{
      var items = Number(maletines) || 0;
      var cap = Number(capacity) || 0;
      
      if(items === 0)return '#000000';
      if(cap > 0 && items > cap)return '#f44336';
      if(items >= 1 && items <= 2)return '#2196f3';
      return '#4caf50';
   };

   return <div style={{ height: '100%', position: 'relative' }}>
      <div style={{
         position: 'absolute',
         top: 15,
         left: '50%',
         transform: 'translateX(-50%)',
         backgroundColor: 'rgba(255, 255, 255, 0.95)',
         backdropFilter: 'blur(4px)',
         zIndex: 10,
         display: 'flex',
         alignItems: 'center',
         gap: 20,
         padding: '8px 18px',
         borderRadius: 24,
         boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
         fontSize: 12,
         color: '#333',
         border: '1px solid rgba(0,0,0,0.06)'
      }}>
         <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ backgroundColor: '#000000', width: 10, height: 10, borderRadius: '50%', display: 'inline-block' }} />
            <span style={{ fontWeight: 600 }}>Sin maletines</span>
         </div>
         <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ backgroundColor: '#f44336', width: 10, height: 10, borderRadius: '50%', display: 'inline-block' }} />
            <span style={{ fontWeight: 600 }}>Capacidad excedida</span>
         </div>
         <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ backgroundColor: '#2196f3', width: 10, height: 10, borderRadius: '50%', display: 'inline-block' }} />
            <span style={{ fontWeight: 600 }}>1 a 2 maletines</span>
         </div>
         <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ backgroundColor: '#4caf50', width: 10, height: 10, borderRadius: '50%', display: 'inline-block' }} />
            <span style={{ fontWeight: 600 }}>3 o más maletines</span>
         </div>
      </div>
      <Button
         icon
         color="white"
         iconName="compress"
         style={{
            position: 'absolute',
            right: 14,
            top: 12,
            zIndex: 12
         }} 
         onClick={resetView}
      />
      <Map
         zoomControl
         defaultZoom={7}
         disableDefaultUI
         mapId="DEMO_MAP_ID"
         clickableIcons={false}
         onClick={() => setSelectedWarehouse(null)}
         defaultCenter={DEFAULT_CENTER}
         gestureHandling="greedy"
         style={{ width: '100%', height: '100%' }}
      >
         {warehouses.map((warehous)=>(
            <AdvancedMarker
               key={'map-'+warehous.warehouse_id} 
               title={warehous.warehouse_name}
               position={{ lat: warehous.location.lattitude, lng: warehous.location.longitude }}
               onClick={()=>{
                  goPosition(warehous.location.lattitude, warehous.location.longitude);
                  setSelectedWarehouse(warehous);
               }}
            >
               <div style={{
                  width: 36, height: 36, 
                  backgroundColor: getMarkerColor(warehous.capacity, warehous.briefcase_items), 
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#fff',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  border: '2px solid white'
               }}>
                  <Icon name="warehouse" style={{ margin: 0, fontSize: 16 }} />
               </div>
            </AdvancedMarker>
         ))}

         {selectedWarehouse && (
            <InfoWindow
               position={{ lat: selectedWarehouse.location.lattitude, lng: selectedWarehouse.location.longitude }}
               onCloseClick={()=>setSelectedWarehouse(null)}
            >
               <div style={{ maxWidth: 260, fontFamily: 'sans-serif', padding: 4 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4, color: '#313385' }}>
                     {selectedWarehouse.warehouse_name}
                  </div>
                  {selectedWarehouse.description && (
                     <div style={{ fontSize: 13, color: '#555', marginBottom: 10, fontStyle: 'italic' }}>
                        {selectedWarehouse.description}
                     </div>
                  )}
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12, backgroundColor: '#f8f9fa', padding: 8, borderRadius: 6 }}>
                     <div style={{ fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
                        <strong>Total equipos:</strong>
                        <span>{selectedWarehouse.total_items || 0}</span>
                     </div>
                     <div style={{ fontSize: 13, display: 'flex', justifyContent: 'space-between', color: '#1b5e20' }}>
                        <strong>Maletines:</strong>
                        <span><strong>{selectedWarehouse.briefcase_items || 0}</strong></span>
                     </div>
                     <div style={{ fontSize: 13, display: 'flex', justifyContent: 'space-between' }}>
                        <strong>Capacidad:</strong>
                        <span>{selectedWarehouse.capacity > 0 ? selectedWarehouse.capacity : 'Sin límite'}</span>
                     </div>
                     
                     {selectedWarehouse.capacity > 0 && (
                        <div style={{ width: '100%', backgroundColor: '#e9ecef', height: 6, borderRadius: 3, marginTop: 4, overflow: 'hidden' }}>
                           <div style={{ 
                              height: '100%', 
                              backgroundColor: getMarkerColor(selectedWarehouse.capacity, selectedWarehouse.briefcase_items),
                              width: `${Math.min(((selectedWarehouse.briefcase_items || 0) / selectedWarehouse.capacity) * 100, 100)}%`
                           }} />
                        </div>
                     )}
                  </div>

                  <div style={{ fontSize: 11, color: '#777', borderTop: '1px solid #eee', paddingTop: 8, paddingBottom: 10 }}>
                     {formatFullAddressMX(selectedWarehouse.location)}
                  </div>
                  
                  <Button 
                     as={Link}
                     size="small" 
                     color="black"
                     iconName="eye"
                     target="_blank"
                     text="Ver detalles"
                     to={`/warehouses/${selectedWarehouse.warehouse_id}`}
                  />
               </div>
            </InfoWindow>
         )}
      </Map>
   </div>
}

export default WarehousesMap;